import { Button, Form, Input, Modal, Popover, Spin, Tree, message } from "antd";
import type { TreeProps } from "antd/es/tree";
import { memo, useState } from "react";
import { useDepartments } from "../hooks/use-departments";
import { useUpdateDisplayOrder } from "../hooks/use-update-display-order";
import { EditOutlined } from "@ant-design/icons";

import styles from "./department-list.module.scss";
import { useUpdateInfoExtras } from "../hooks/use-update-info-extras";
import DepartmentDescTooltip from "./department-desc-tooltip";
import { generateTitleMoveConfirmModal } from "../../../document/utils/generateTitleMoveConfirmModal";

const UpdateDescriptionDeptForm = ({ close, department }: { close: () => void; department: any }) => {
  const { mutate: updateInfoExtras, isLoading: isUpdateInfoExtrasLoading, invalidate } = useUpdateInfoExtras();

  const onFinish = (values: { description: string }) => {
    updateInfoExtras(
      { departmentId: department.id, info: values.description },
      {
        onSuccess() {
          message.success("Cập nhật mô tả thành công");
          invalidate();
          close();
        },
        onError() {
          message.error("Cập nhật mô tả thất bại");
        },
      },
    );
  };

  const loading = isUpdateInfoExtrasLoading;

  return (
    <Spin spinning={loading}>
      <Form onFinish={onFinish} initialValues={{ description: department.infoExtras }}>
        <Form.Item name="description">
          <Input placeholder="Mô tả..." />
        </Form.Item>
        <div style={{ display: "flex", justifyContent: "end", gap: 2 }}>
          <Button size="small" onClick={close}>
            Hủy
          </Button>
          <Button htmlType="submit" type="primary" size="small">
            Xác nhận
          </Button>
        </div>
      </Form>
    </Spin>
  );
};

const UpdateDescriptionDeptPopover = ({ department }: { department: any }) => {
  const [openPopover, setOpenPopover] = useState(false);

  const closePopover = (visible: boolean) => setOpenPopover(visible);

  return (
    <Popover
      open={openPopover}
      destroyTooltipOnHide
      content={<UpdateDescriptionDeptForm department={department} close={() => closePopover(false)} />}
      title="Mô tả"
      trigger={["click"]}
      onOpenChange={closePopover}
    >
      <EditOutlined rev="" />
    </Popover>
  );
};

const DepartmentList = () => {
  const [loadingState, setLoadingState] = useState(false);
  const [modal, contextHolder] = Modal.useModal();

  const { treeData = [], isLoading: isDeparmtentLoading, data: departments = [], hashedData } = useDepartments();
  const { mutateAsync: updateDisplayOrderAsync, invalidate } = useUpdateDisplayOrder();

  const updateDepartmentHandler = async (params: { parentId: number; nodeId: number; displayOrder: number }) => {
    try {
      setLoadingState(true);
      await updateDisplayOrderAsync({
        parentId: params.parentId,
        info: [{ id: params.nodeId, displayOrder: params.displayOrder }],
      });
      invalidate();
      message.success("Cập nhật vị trí thành công");
    } catch (error) {
      message.error("Cập nhật vị trí thất bại");
    } finally {
      setLoadingState(false);
    }
  };

  const onDrop: TreeProps["onDrop"] = async (info) => {
    const title = generateTitleMoveConfirmModal(info);
    modal.confirm({
      title: title,
      okText: "Xác nhận",
      cancelText: "Hủy",
      onOk: async () => {
        let parentId: number = 0;
        let newDisplayOrder: number = 0;
        const dropNode: any = info.node;
        const dragNode: any = info.dragNode;
        const dropPos = info.node.pos.split("-");
        const dropPosition = info.dropPosition - Number(dropPos[dropPos.length - 1]);

        if (dropPosition === -1) {
          newDisplayOrder = departments[0].displayOrder / 2;
          parentId = 0;
        }

        if (dropPosition === 0) {
          parentId = dropNode.id;
          if (!dropNode?.children || dropNode?.children?.length === 0) {
            newDisplayOrder = 1;
          } else {
            const min = Math.min(...(dropNode?.children?.map((item: any) => item.displayOrder) ?? []));
            newDisplayOrder = min / 2;
          }
        }

        if (dropPosition === 1) {
          let children = [];
          const parentNode = hashedData.get(dropNode.parentId);

          children = !parentNode ? departments : parentNode?.viagsDepartmentChild ?? [];
          parentId = dropNode.parentId;

          const dropNodeIdx = children.findIndex((item: any) => item.id === dropNode.id);

          const prevNode = children[dropNodeIdx];
          const nextNode = children[dropNodeIdx + 1];

          if (!nextNode) {
            newDisplayOrder = prevNode.displayOrder + 1;
          } else {
            newDisplayOrder = (prevNode.displayOrder + nextNode.displayOrder) / 2;
          }
        }

        updateDepartmentHandler({ parentId, nodeId: dragNode.key, displayOrder: newDisplayOrder });
      },
    });
  };

  const loading = isDeparmtentLoading || loadingState;

  return (
    <Spin spinning={loading}>
      {contextHolder}
      <Tree
        defaultExpandAll
        draggable
        blockNode
        onDrop={onDrop}
        treeData={treeData}
        height={670}
        titleRender={(node) => (
          <div className={styles.titleContainer}>
            <span>{node.title}</span>
            <DepartmentDescTooltip desc={(node as any).infoExtras} />
            <div className={styles.titleEditIcon}>
              <UpdateDescriptionDeptPopover department={node} />
            </div>
          </div>
        )}
      />
    </Spin>
  );
};

export default memo(DepartmentList);
